import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  CommandEmpty,
  CommandGroup,
  CommandGroupLabel,
  CommandItem,
  Icon,
  Kbd,
  KbdGroup,
  Text,
  Toggle,
  toastManager,
  useIsMobile,
  useTheme,
} from "@salestrak/ui";
import { usePathnameFlag } from "#app/lib/hooks/router-compat";
import type { EntityData } from "#app/lib/entity-visuals";
import {
  useResolveAddressSuggestion,
} from "#app/lib/hooks/use-resolve-address-suggestion";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useEntityDetailOptional } from "#app/components/entity";
import { useIssueViewOptional } from "#app/components/issue-view";
import {
  issueHref as issueLink,
  teamHref as teamLink,
  workflowHref as workflowLink,
} from "#app/lib/utils/entity-hrefs";
import {
  useGlobalSearch,
  KIND_TO_SECTION,
  ALL_KINDS,
  type GlobalSearchHit,
  type SearchKind,
} from "#app/lib/hooks/use-global-search";
import {
  useRecentItems,
  type RecentItem,
} from "#app/lib/hooks/use-recent-items";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  useManageDialogDoors,
} from "#app/components/navigation/use-manage-dialog-doors";
import {
  MODULE_HOTKEY_LEAD,
} from "#app/components/navigation/module-rail/tiles";
import { useCanCreateProject } from "#app/lib/hooks/use-can-create-project";
import { useViewerCanOpenProfiles } from "#app/lib/hooks/use-permission";
import {
  useAutomationsDialogActions,
} from "#app/lib/automations-dialog-context";
import {
  useCustomFieldsDialogActions,
} from "#app/lib/custom-fields-dialog-context";
import {
  useIntegrationsDialogActions,
} from "#app/lib/integrations-dialog-context";
import { useAnalytics, useTrackSearch, AnalyticsContext, SearchEvent } from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";
import {
  SEARCH_KIND_META,
  SEARCH_FILTERS,
  ADDRESS_ACTION_LABEL,
  RESULTS_COUNT,
  BROWSE_COUNT,
} from "./search-kinds";
import { useSearchChipFilters } from "./use-search-chip-filters";
import { useCommands, filterCommands, type CommandDef } from "./commands";
import {
  copyUrl,
  ROW_VISUAL_BOX,
  RowActions,
  SearchEmptyState,
  SearchResultsSkeleton,
  ResultRow,
  AddressRow,
} from "./rows";

export type GlobalSearchContent = Omittable<{
  query: string;
  setQuery: (value: string) => void;
  /** Results + commands list, to render inside a CommandList. */
  itemsContent: React.ReactNode;
  /** Scoped-search header (the kind filter chips). Rendered above
   *  the list, in flow. */
  scopeBar: React.ReactNode;
  /** Short context label for the highlighted row (e.g. "Project", "Navigate"),
   *  shown on the footer's right side. */
  footerContext?: string;
  /** The verb the footer legend pairs with Enter: "open" for every row but
   *  an address the viewer can create at, which says "create". */
  footerEnterVerb: "open" | "create";
  /** Wire onto the Command root so the hook can track the highlighted row. */
  onItemHighlighted: (value: unknown) => void;
  /** Attach to the CommandInput: ⌘/Ctrl+Enter opens the highlighted row in a
   *  new tab, ⌘/Ctrl+C copies its URL (rows with a URL only), mirroring the
   *  row's hover actions. */
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Attach to the CommandList so a new search scrolls back to the top. */
  listRef: React.RefObject<HTMLDivElement | null>;
  feedbackOpen: boolean;
  setFeedbackOpen: (open: boolean) => void;
}>;

/**
 * Shared controller for the global search surfaces (desktop dialog and mobile
 * drawer). Owns the query, the command list, and the result/command
 * rendering. Every selectable action calls `onClose()` first so each surface can
 * dismiss itself (clear+blur the bar on desktop; close the drawer on mobile).
 */
export function useGlobalSearchContent({
  onClose,
  focusInput,
}: Omittable<{
  onClose: () => void;
  /** Return focus to the search input (e.g. after a chip click) so keyboard
   *  navigation keeps working — Base UI binds it to the input. */
  focusInput?: () => void;
}>): GlobalSearchContent {
  const { track } = useAnalytics();
  const issueView = useIssueViewOptional();
  // Creating a project takes create.items AND an editable workflow — the same
  // gate every other create affordance uses. Without it the command hides and
  // an address pick only pans the map, never opening a draft that cannot save.
  const canCreateProject = useCanCreateProject() && issueView != null;
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const { push: appPush } = useAppNavigate();
  const { theme, setTheme } = useTheme();
  const isMobile = useIsMobile();
  // Whether selecting an address also opens a project draft there — the
  // same gate `selectAddress` applies. Every place that NAMES the action
  // (footer context, legend verb, ↵ tooltip) reads this one value, so a
  // viewer who cannot create is never promised a project.
  const addressCreates = !isMobile && canCreateProject;
  const addressActionLabel = addressCreates
    ? ADDRESS_ACTION_LABEL.create
    : ADDRESS_ACTION_LABEL.open;
  // Selecting a project pans the map (instead of opening the sidebar) only when
  // the search is invoked from the map screen — see navigateToIssue.
  const onMap = usePathnameFlag((pathname) => pathname.endsWith("/map"));

  // Base UI owns the highlight; `onItemHighlighted` mirrors it here so rows can
  // render their selected affordances and the footer can label the active row.
  // The highlight follows BOTH arrow keys and pointer hover.
  const [highlightedValue, setHighlightedValue] = useState<string | null>(null);
  const onItemHighlighted = useCallback((value: unknown) => {
    setHighlightedValue(typeof value === "string" ? value : null);
  }, []);

  // Scoped search: the kind filter chips.
  const { storedKinds, setSelectedKinds } = useSearchChipFilters();
  // A Guest has no member kind at all: no chip, no member hits, no browse
  // list — profiles are withheld from them (useViewerCanOpenProfiles). A
  // stored "user" chip from a roomier role is ignored; the next chip press
  // rewrites the setting without it.
  const canOpenProfiles = useViewerCanOpenProfiles();
  const availableFilters = useMemo(
    () =>
      canOpenProfiles
        ? SEARCH_FILTERS
        : SEARCH_FILTERS.filter((f) => f.value !== "user"),
    [canOpenProfiles],
  );
  const selectedKinds = useMemo(
    () =>
      canOpenProfiles ? storedKinds : storedKinds.filter((k) => k !== "user"),
    [canOpenProfiles, storedKinds],
  );
  const toggleKind = useCallback(
    (kind: SearchKind) => {
      const next = selectedKinds.includes(kind)
        ? selectedKinds.filter((entry) => entry !== kind)
        : [...selectedKinds, kind];
      setSelectedKinds(next);
      track({
        context: AnalyticsContext.Search,
        event: SearchEvent.ToggleKind,
        data: { kind, enabled: next.includes(kind) },
      });
      // Toggling a chip moves focus to it; hand focus back to the input so
      // arrow-key navigation (handled on the input) keeps working.
      focusInput?.();
    },
    [selectedKinds, setSelectedKinds, focusInput, track],
  );
  // The empty states' "Clear filters": drop every kind chip (widening a
  // no-match search to all kinds / returning browse to the launcher).
  const clearFilters = useCallback(() => {
    setSelectedKinds([]);
    focusInput?.();
  }, [setSelectedKinds, focusInput]);
  // The server returns hits grouped by kind (no ranking); each section (items +
  // total) paginates independently via the hook's `loadMore`.
  const { query, setQuery, debouncedQuery, results, loadMore, loading } =
    useGlobalSearch({
      // Only fetch the kinds actually in view — a filtered-out kind (incl. the
      // paid Google Places addresses call) is never queried. An empty list
      // means every kind, so a viewer without the member kind names theirs.
      kinds:
        selectedKinds.length > 0 || canOpenProfiles
          ? selectedKinds
          : availableFilters.map((f) => f.value),
    });

  // One `search.search` per committed query; never the term. A "/" query
  // is command mode, not a search.
  useTrackSearch(query.trimStart().startsWith("/") ? "" : query, () => ({
    context: AnalyticsContext.Search,
    event: SearchEvent.Search,
    data: { kindCount: selectedKinds.length, onMap },
  }));

  const section = useCallback(
    (kind: SearchKind) => results[KIND_TO_SECTION[kind]],
    [results],
  );

  // Sections to render: all when no chips are selected, else just the selected
  // ones. Preserves SEARCH_FILTERS order.
  const visibleFilters =
    selectedKinds.length === 0
      ? availableFilters
      : availableFilters.filter((f) => selectedKinds.includes(f.value));

  // A genuine new result set — a new query — scrolls back to the top. "Load
  // more" appends below and must NOT move the viewport, so this keys on the
  // query only. The list lives inside the library's ScrollArea, so the
  // scrolling element is that viewport, not the list itself.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const viewport = listRef.current?.closest<HTMLElement>(
      "[data-slot=scroll-area-viewport]",
    );
    if (viewport) viewport.scrollTop = 0;
  }, [debouncedQuery]);

  // "Load more" fetches the section's next page server-side, then returns focus
  // to the input so arrow-key navigation keeps working.
  const onLoadMore = useCallback(
    (kind: SearchKind) => {
      loadMore(kind);
      focusInput?.();
    },
    [loadMore, focusInput],
  );

  // Members/teams open the shared entity detail (same as clicking the chip).
  const entityDetail = useEntityDetailOptional();

  // Selecting an address resolves the Google place to lat/lng, then: (1) opens
  // the map focused there — finds-or-creates a pin and pops the infobox (the
  // map's `focusLat`/`focusLng` one-shot) — and, on desktop only, (2) opens the
  // create-project issue-view modal (the sidebar) prefilled with the address. On
  // mobile we just land on the map (no create sheet). Falls back to the plain
  // map on error.
  const resolveAddress = useResolveAddressSuggestion();
  const selectAddress = useCallback(
    async (placeId: string, description: string) => {
      onClose();
      try {
        const resolved = await resolveAddress(placeId, description);
        if (resolved?.latLng) {
          const params = new URLSearchParams({
            focusLat: String(resolved.latLng.lat),
            focusLng: String(resolved.latLng.lng),
          });
          if (resolved.addressId) params.set("addressId", resolved.addressId);
          appPush(`/map?${params.toString()}`);
          if (!isMobile && canCreateProject) {
            // Prefill the title from the Melissa property record, same as the
            // map's "add project" action (map.tsx handleAddProject). The
            // ResolveAddressSuggestion mutation already selects propertyDetails
            // — it was simply never read here, so picking an address in global
            // search opened an untitled draft while the identical action on the
            // map arrived pre-titled with the owner's name.
            const ownerName = resolved.propertyDetails?.ownerNames?.[0];
            issueView?.openCreateIssue({
              source: "global_search",
              address: {
                wholeAddress: resolved.wholeAddress ?? description,
                latLng: { lat: resolved.latLng.lat, lng: resolved.latLng.lng },
              },
              name: ownerName || resolved.wholeAddress || description,
            });
          }
          return;
        }
        // Resolved but no coordinates — tell the user rather than silently
        // dropping them on the default map with no sign the pick failed.
        toastManager.add({
          title: "Address not found",
          type: "error",
        });
      } catch (err) {
        toastMutationError(err, "Failed to open address");
      }
      appPush("/map");
    },
    [onClose, resolveAddress, appPush, issueView, isMobile, canCreateProject],
  );

  const onOpenFeedback = useCallback(() => {
    onClose();
    setFeedbackOpen(true);
  }, [onClose]);

  // `search.command` for this row is sent by the command renderer.
  const onCreateIssue = useCallback(() => {
    onClose();
    issueView?.openCreateIssue({ source: "global_search" });
  }, [onClose, issueView]);

  const navigate = useCallback(
    (route: string) => {
      onClose();
      appPush(route);
    },
    [onClose, appPush],
  );

  const navigateToIssue = useCallback(
    (
      workflowId: string,
      issueId: string,
      coords?: { lat: number | null; lng: number | null },
    ) => {
      onClose();
      // When searching from the map, selecting a project pans the map to its
      // pin instead of opening the project sidebar — the map's `focusLat`/`focusLng` one-shot finds-or-
      // creates the pin and pops its infobox. Anywhere else, open the project.
      if (
        onMap &&
        coords &&
        Number.isFinite(coords.lat) &&
        Number.isFinite(coords.lng)
      ) {
        appPush(`/map?focusLat=${coords.lat}&focusLng=${coords.lng}`);
        return;
      }
      if (issueView?.openIssueById) {
        issueView.openIssueById(issueId, { source: "search" });
      } else {
        appPush(`/workflow/${workflowId}?issue=${issueId}`);
      }
    },
    [onClose, appPush, issueView, onMap],
  );

  const toggleTheme = useCallback(() => {
    onClose();
    setTheme(theme === "dark" ? "light" : "dark");
  }, [onClose, theme, setTheme]);

  // The manager dialogs open over whatever screen the search was invoked from;
  // the search dismisses first so the dialog is the only overlay left up.
  const { showCustomFields, showAutomations, showIntegrations } =
    useManageDialogDoors();
  const { openDialog: openCustomFieldsDialog } = useCustomFieldsDialogActions();
  const { openDialog: openAutomationsDialog } = useAutomationsDialogActions();
  const { openDialog: openIntegrationsDialog } = useIntegrationsDialogActions();
  const openCustomFields = useCallback(() => {
    onClose();
    openCustomFieldsDialog();
  }, [onClose, openCustomFieldsDialog]);
  const openAutomations = useCallback(() => {
    onClose();
    openAutomationsDialog();
  }, [onClose, openAutomationsDialog]);
  const openAppCenter = useCallback(() => {
    onClose();
    openIntegrationsDialog();
  }, [onClose, openIntegrationsDialog]);

  const themeLabel = useMemo(
    () => `Toggle Theme to ${theme === "dark" ? "Light" : "Dark"}`,
    [theme],
  );

  const commands = useCommands({
    navigate,
    openCreateIssue: canCreateProject ? onCreateIssue : undefined,
    openCustomFields: showCustomFields ? openCustomFields : undefined,
    openAutomations: showAutomations ? openAutomations : undefined,
    openAppCenter: showIntegrations ? openAppCenter : undefined,
    toggleTheme,
    openFeedback: onOpenFeedback,
    themeLabel,
  });

  // A leading "/" is command mode: every command shows (the launcher layout),
  // narrowed by whatever follows the slash. The search hook never fetches for
  // a slash query, so `debouncedQuery` stays empty and the launcher branch
  // renders — only the filter text needs the slash stripped.
  const commandMode = query.trimStart().startsWith("/");
  const commandFilter = commandMode ? query.trimStart().slice(1) : query;
  const filteredCommands = useMemo(
    () => filterCommands(commands, commandFilter),
    [commands, commandFilter],
  );
  const showResults = debouncedQuery.length > 0;

  const { workflows, workflowMap, users, teams } = useWorkspaceData();
  const { items: recentIssues } = useRecentItems();
  // id → last-visited epoch ms: drives both the clock marker and the row's
  // "· 19m ago" meta on typed-search hits the user has opened recently.
  const recentVisitedAt = useMemo(
    () => new Map(recentIssues.map((r) => [r.id, r.visitedAt])),
    [recentIssues],
  );

  // Absolute URLs for the row actions (new tab / copy link) — same shape the
  // issue card's "Copy link" builds, workspace-qualified.
  const { activeWorkspace } = useWorkspace();
  const workspacePath = activeWorkspace?.urlPath ?? "";
  const issueHref = useCallback(
    (workflowId: string, issueId: string) => issueLink(workspacePath, workflowId, issueId),
    [workspacePath],
  );
  const workflowHref = useCallback(
    (workflowId: string) => workflowLink(workspacePath, workflowId),
    [workspacePath],
  );
  const teamHref = useCallback(
    (teamId: string) => teamLink(workspacePath, teamId),
    [workspacePath],
  );

  // The row meta line: `in <workflow> · 19m ago`, either part optional.
  const issueMeta = useCallback(
    (workflowId: string | null | undefined, visitedAt: number | undefined) => {
      const workflowName = workflowId
        ? workflowMap.get(workflowId)?.name
        : undefined;
      const parts = [
        workflowName ? `in ${workflowName}` : null,
        visitedAt != null
          ? formatRelativeDate(new Date(visitedAt).toISOString(), "compact")
          : null,
      ].filter(Boolean);
      return parts.length ? parts.join(" · ") : undefined;
    },
    [workflowMap],
  );

  // The launcher's "Results" section: recently-opened projects, newest first —
  // at most RESULTS_COUNT shown (the store already applies the recency
  // window). With nothing recent it falls back to the workspace's default
  // workflow (the flagged one, else the first) as the single result — since
  // workspaces are seeded with a default workflow, the section always has
  // something to show.
  const recentResults = useMemo(
    () => recentIssues.slice(0, RESULTS_COUNT),
    [recentIssues],
  );
  const fallbackWorkflow = useMemo(
    () => workflows.find((w) => w.isDefault) ?? workflows[0],
    [workflows],
  );

  // Workflows in typed search are matched CLIENT-side (name or key) — the
  // server's search kinds are project/member/team only, and every workflow is
  // already loaded in workspace data. No chip exists for them, so any chip
  // selection (a narrowed search) hides the section, matching "no selection =
  // everything". Keyed to debouncedQuery so the section lands with the server
  // sections instead of leading them by a keystroke.
  const matchedWorkflows = useMemo(() => {
    if (debouncedQuery.length === 0 || selectedKinds.length > 0) return [];
    const q = debouncedQuery.toLowerCase();
    return workflows
      .filter(
        (w) =>
          (w.name ?? "").toLowerCase().includes(q) ||
          (w.key ?? "").toLowerCase().includes(q),
      )
      .slice(0, RESULTS_COUNT);
  }, [debouncedQuery, selectedKinds, workflows]);

  // Chip browse (empty query + selected chips): list the kind straight from
  // workspace data — every member and team is already client-loaded, so no
  // fetch. Alphabetical, capped at BROWSE_COUNT.
  const browsing = query.trim().length === 0 && selectedKinds.length > 0;
  const browseUsers = useMemo(() => {
    if (!browsing || !selectedKinds.includes("user")) return [];
    return [...users]
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
      .slice(0, BROWSE_COUNT);
  }, [browsing, selectedKinds, users]);
  const browseTeams = useMemo(() => {
    if (!browsing || !selectedKinds.includes("team")) return [];
    return [...teams]
      .sort((a, b) => (a.teamName ?? "").localeCompare(b.teamName ?? ""))
      .slice(0, BROWSE_COUNT);
  }, [browsing, selectedKinds, teams]);

  // Maps each rendered CommandItem value to a short context label the footer
  // shows on the right for whatever row is highlighted, labelled by kind.
  const contextByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const kind of ALL_KINDS) {
      for (const hit of results[KIND_TO_SECTION[kind]].items) {
        map[`${hit.kind}-${hit.id}`] =
          hit.kind === "address"
            ? addressActionLabel
            : SEARCH_KIND_META[hit.kind as SearchKind].singular;
      }
    }
    for (const cmd of commands) {
      map[cmd.id] = cmd.meta && cmd.meta !== "action" ? "Navigate" : "Command";
    }
    // The launcher's Results rows (recents + the default-workflow fallback),
    // the typed-search workflow matches, and the chip-browse lists.
    for (const rec of recentIssues) map[`issue-${rec.id}`] = "Project";
    if (fallbackWorkflow) {
      map[`workflow-${fallbackWorkflow.workflowId}`] = "Workflow";
    }
    for (const w of matchedWorkflows) {
      map[`workflow-${w.workflowId}`] = "Workflow";
    }
    for (const u of browseUsers) map[`user-${u.userId}`] = "Member";
    for (const t of browseTeams) map[`team-${t.teamId}`] = "Team";
    return map;
  }, [
    addressActionLabel,
    results,
    commands,
    recentIssues,
    fallbackWorkflow,
    matchedWorkflows,
    browseUsers,
    browseTeams,
  ]);

  // Absolute URL per rendered row value — powers the ⌘/Ctrl+Enter (new tab)
  // and ⌘/Ctrl+C (copy URL) shortcuts for whatever row is highlighted. Only
  // rows with a real route (projects, workflows, teams) appear; overlay rows
  // (members), addresses and commands have no URL and fall through to
  // nothing.
  const urlByValue = useMemo(() => {
    const map: Record<string, string> = {};
    for (const hit of results.issues.items) {
      if (hit.workflowId)
        map[`issue-${hit.id}`] = issueHref(hit.workflowId, hit.id);
    }
    for (const hit of results.teams.items) {
      map[`team-${hit.id}`] = teamHref(hit.id);
    }
    for (const t of browseTeams) map[`team-${t.teamId}`] = teamHref(t.teamId);
    for (const rec of recentIssues) {
      if (rec.workflowId)
        map[`issue-${rec.id}`] = issueHref(rec.workflowId, rec.id);
    }
    if (fallbackWorkflow) {
      map[`workflow-${fallbackWorkflow.workflowId}`] = workflowHref(
        fallbackWorkflow.workflowId,
      );
    }
    for (const w of matchedWorkflows) {
      map[`workflow-${w.workflowId}`] = workflowHref(w.workflowId);
    }
    return map;
  }, [
    results.issues.items,
    results.teams.items,
    browseTeams,
    recentIssues,
    fallbackWorkflow,
    matchedWorkflows,
    issueHref,
    workflowHref,
    teamHref,
  ]);

  // ⌘/Ctrl+Enter opens the highlighted row in a new tab; ⌘/Ctrl+C copies its
  // URL. Both leave the search open (same contract as the row buttons) and
  // only bite on rows with a URL. preventDefault keeps Base UI's plain-Enter
  // select (and the browser's copy) from also firing. Escape clears FIRST:
  // with a query it wipes the text and stays open (the clear chip's hotkey);
  // only an empty-query Escape falls through to each surface's close.
  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape" && query.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        setQuery("");
        return;
      }
      if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey) return;
      const isEnter = e.key === "Enter";
      // Lowercased — with CapsLock on, e.key is "C".
      const isCopy = e.key.toLowerCase() === "c";
      if (!isEnter && !isCopy) return;
      // Selected query text keeps native ⌘C — the hotkey only takes over
      // when a copy would otherwise be a no-op.
      if (
        isCopy &&
        e.currentTarget.selectionStart !== e.currentTarget.selectionEnd
      ) {
        return;
      }
      const url = highlightedValue ? urlByValue[highlightedValue] : undefined;
      if (!url) return;
      e.preventDefault();
      e.stopPropagation();
      if (isEnter) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        copyUrl(url);
      }
    },
    [highlightedValue, urlByValue, query, setQuery],
  );

  // One renderer for every hit. The server groups by kind and returns each
  // section in source order, so we render in order and just dispatch the chip +
  // select behaviour by kind.
  // `search.select_result`: the row's kind, and whether it came from the
  // typed results or the launcher's recents and browse rows.
  const trackSelect = (kind: string, from: "results" | "recent" | "browse") =>
    track({
      context: AnalyticsContext.Search,
      event: SearchEvent.SelectResult,
      label: "global_search",
      data: { kind, from, onMap },
    });

  const renderHit = (hit: GlobalSearchHit, index: number) => {
    const value = `${hit.kind}-${hit.id}`;
    // React key appends the array index — server-aggregated cross-kind hits can
    // carry duplicate ids (dedup gaps across sources), and keying purely by id
    // would then throw "two children with the same key".
    const key = `${value}-${index}`;
    const description = hit.subtitle ?? undefined;
    const highlighted = highlightedValue === value;

    // Addresses are Google Places suggestions (not entities): a map-pin + text
    // row that resolves + opens the map on select.
    if (hit.kind === "address") {
      return (
        <AddressRow
          key={key}
          value={value}
          highlighted={highlighted}
          description={hit.name}
          actionLabel={addressActionLabel}
          onSelect={() => {
            trackSelect("address", "results");
            void selectAddress(hit.id, hit.name);
          }}
        />
      );
    }

    if (hit.kind === "issue") {
      const visitedAt = recentVisitedAt.get(hit.id);
      const entity: EntityData = {
        kind: "issue",
        id: hit.id,
        issue: { name: hit.name, icon: { icon: hit.iconName, color: hit.iconColor } },
        workflowId: hit.workflowId ?? undefined,
      };
      const open = () => {
        trackSelect("issue", "results");
        navigateToIssue(hit.workflowId ?? "", hit.id, {
          lat: hit.lat ?? null,
          lng: hit.lng ?? null,
        });
      };
      return (
        <ResultRow
          key={key}
          value={value}
          highlighted={highlighted}
          entity={entity}
          description={description}
          meta={issueMeta(hit.workflowId, visitedAt)}
          actions={
            <RowActions
              url={
                hit.workflowId ? issueHref(hit.workflowId, hit.id) : undefined
              }
              onOpen={open}
            />
          }
          onSelect={open}
        />
      );
    }

    // Member / team rows — rendered through the entity chip; selecting opens
    // that entity's detail: the profile island for a member, the team page
    // for a team.
    const entity: EntityData =
      hit.kind === "team"
        ? {
            kind: "team",
            id: hit.id,
            team: { teamName: hit.name, icon: { icon: hit.iconName, color: hit.iconColor } },
          }
        : {
            kind: "user",
            id: hit.id,
            user: { name: hit.name, email: hit.subtitle, profileImage: hit.profileImage },
          };
    // Member detail is an overlay, not a route — no URL, so ↵ only. A team
    // is a route, so its row gets the new-tab/copy pair.
    const openEntity = () => {
      trackSelect(hit.kind, "results");
      onClose();
      entityDetail?.openEntity(hit.kind === "team" ? "team" : "user", hit.id, {
        via: "search",
      });
    };
    return (
      <ResultRow
        key={key}
        value={value}
        highlighted={highlighted}
        entity={entity}
        description={description}
        actions={
          <RowActions
            url={hit.kind === "team" ? teamHref(hit.id) : undefined}
            onOpen={openEntity}
          />
        }
        onSelect={openEntity}
      />
    );
  };

  // A workflow row — the typed-search "Workflows" section and the launcher's
  // default-workflow fallback share it. Selecting one opens its board.
  const renderWorkflowResult = (workflow: (typeof workflows)[number]) => (
    <ResultRow
      key={`workflow-${workflow.workflowId}`}
      value={`workflow-${workflow.workflowId}`}
      highlighted={highlightedValue === `workflow-${workflow.workflowId}`}
      entity={{ kind: "workflow", id: workflow.workflowId, workflow }}
      actions={
        <RowActions
          url={workflowHref(workflow.workflowId)}
          onOpen={() => {
            trackSelect("workflow", showResults ? "results" : "browse");
            navigate(`/workflow/${workflow.workflowId}`);
          }}
        />
      }
      onSelect={() => {
        trackSelect("workflow", showResults ? "results" : "browse");
        navigate(`/workflow/${workflow.workflowId}`);
      }}
    />
  );

  // A launcher "Results" row: a recently-opened project, rendered from its
  // stored snapshot (no fetch). Same row grammar as a search hit.
  const renderRecentResult = (rec: RecentItem) => {
    const open = () => {
      trackSelect("issue", "recent");
      navigateToIssue(rec.workflowId ?? "", rec.id);
    };
    return (
      <ResultRow
        key={`recent-${rec.id}`}
        value={`issue-${rec.id}`}
        highlighted={highlightedValue === `issue-${rec.id}`}
        entity={{
          kind: "issue",
          id: rec.id,
          issue: {
            name: rec.name,
            icon: { icon: rec.iconName, color: rec.iconColor },
          },
          workflowId: rec.workflowId ?? undefined,
        }}
        meta={issueMeta(rec.workflowId, rec.visitedAt)}
        actions={
          <RowActions
            url={rec.workflowId ? issueHref(rec.workflowId, rec.id) : undefined}
            onOpen={open}
          />
        }
        onSelect={open}
      />
    );
  };

  // Chip-browse rows: a member / team straight from workspace data, same row
  // grammar and select behaviour as their typed-search hits (entity detail
  // overlay — no URL, so ↵ only).
  const renderBrowseUser = (user: (typeof users)[number]) => {
    const open = () => {
      trackSelect("user", "browse");
      onClose();
      entityDetail?.openEntity("user", user.userId, { via: "search" });
    };
    return (
      <ResultRow
        key={`browse-user-${user.userId}`}
        value={`user-${user.userId}`}
        highlighted={highlightedValue === `user-${user.userId}`}
        entity={{
          kind: "user",
          id: user.userId,
          user: {
            name: user.name,
            email: user.email,
            profileImage: user.profileImage,
          },
        }}
        description={user.email ?? undefined}
        actions={<RowActions onOpen={open} />}
        onSelect={open}
      />
    );
  };
  const renderBrowseTeam = (team: (typeof teams)[number]) => {
    const open = () => {
      trackSelect("team", "browse");
      onClose();
      entityDetail?.openEntity("team", team.teamId, { via: "search" });
    };
    return (
      <ResultRow
        key={`browse-team-${team.teamId}`}
        value={`team-${team.teamId}`}
        highlighted={highlightedValue === `team-${team.teamId}`}
        entity={{ kind: "team", id: team.teamId, team }}
        actions={<RowActions url={teamHref(team.teamId)} onOpen={open} />}
        onSelect={open}
      />
    );
  };

  // Every command renders as a keyboard-navigable CommandItem row: a bare
  // muted glyph on the same 28px rail the entity and address rows use — no
  // tile behind it, so the glyph column reads as one column whatever the
  // row kind — plus a single-line label.
  const renderCommand = (cmd: CommandDef, index: number) => {
    const highlighted = highlightedValue === cmd.id;
    // One `search.command` per run, whichever row or result list ran it.
    const run = () => {
      track({
        context: AnalyticsContext.Search,
        event: SearchEvent.Command,
        label: cmd.id,
        data: { mode: commandMode ? "slash" : showResults ? "results" : "launcher" },
      });
      cmd.action();
    };
    return (
      // Key appends the array index per the list-key rule (never key purely by id).
      <CommandItem key={`${cmd.id}-${index}`} value={cmd.id} onClick={run}>
        <Box direction="row" align="center" gap="sm" fill="width" justify="between">
          <Box direction="row" align="center" gap="sm" shrink minWidth={0}>
            <Box
              width={ROW_VISUAL_BOX}
              height={ROW_VISUAL_BOX}
              align="center"
              justify="center"
              shrink={false}
            >
              <Icon name={cmd.icon} size="sm" color="muted" />
            </Box>
            <Text size="sm" truncate={1}>
              {cmd.label}
            </Text>
          </Box>
          {/* At rest: no route text, no chevron — a "/" query already finds
              commands by name — only the `G` chord for destinations that
              have one, read as two presses like the rail tooltip. On
              highlight, the ↵ only: no URL to copy or open elsewhere. */}
          {highlighted ? (
            <RowActions onOpen={run} />
          ) : cmd.hotkey ? (
            <KbdGroup>
              <Kbd>{MODULE_HOTKEY_LEAD}</Kbd>
              <Text size="2xs" variant="muted">
                then
              </Text>
              <Kbd>{cmd.hotkey}</Kbd>
            </KbdGroup>
          ) : null}
        </Box>
      </CommandItem>
    );
  };

  // Entity hits and client-matched workflows for the typed query — the
  // "Results" group. Commands are counted separately: a matching command
  // alone is a full answer (no empty state), but it never stands in for hits
  // while they are still loading.
  const hasHits =
    matchedWorkflows.length > 0 ||
    visibleFilters.some((f) => section(f.value).items.length > 0);
  const hasAnyResult = hasHits || filteredCommands.length > 0;

  const itemsContent = showResults ? (
    // Keyed by the selected chips so changing the filter REMOUNTS the whole
    // list: items re-register in DOM order, so keyboard-nav order matches what
    // is on screen. "Load more" only grows a section's shown count (no
    // remount), so it leaves the highlight alone.
    (<React.Fragment key={`filter-${selectedKinds.join(",")}`}>
      {/* Matching commands lead, above the hits. A command is matched on its
          own name, so a hit on "cust" means the user typed the command's
          name — the most likely intent — and it must be the first Enter
          target, never displaced by however many entity rows also contain
          the letters. Commands are few and client-side, so this block is
          instant while the hits are still fetching. */}
      {filteredCommands.length > 0 && (
        <CommandGroup>
          <CommandGroupLabel>Commands</CommandGroupLabel>
          {filteredCommands.map(renderCommand)}
        </CommandGroup>
      )}
      {/* ONE flat "Results" group: every kind in chip order, workflows riding
          under the project hits. The per-kind headings went with the
          flattening, so each "Load more" row names its kind to keep the
          scent. */}
      {hasHits && (
        <CommandGroup>
          <CommandGroupLabel>Results</CommandGroupLabel>
          {visibleFilters.map(({ value: kind, plural }) => {
            const { items, total } = section(kind);
            return (
              <React.Fragment key={kind}>
                {items.map(renderHit)}
                {items.length < total && (
                  <CommandItem
                    // Key on the section's contents so a new search / page
                    // REMOUNTS this row — otherwise it persists with a stale
                    // registration order and sorts above the fresh result rows.
                    key={`more-${kind}-${items[0]?.id ?? ""}-${items.length}`}
                    value={`more-${kind}`}
                    onClick={() => {
                      track({
                        context: AnalyticsContext.Search,
                        event: SearchEvent.LoadMore,
                        data: { kind },
                      });
                      onLoadMore(kind);
                    }}
                  >
                    <Box direction="row" align="center" gap="xs">
                      <Icon name="chevron-down" size="xs" color="muted" />
                      <Text size="sm" variant="muted">
                        {`Load more ${plural}`}
                      </Text>
                    </Box>
                  </CommandItem>
                )}
                {/* The client-matched workflows ride directly under Projects
                    (even when no project matched) — they have no server
                    section or chip of their own. */}
                {kind === "issue" && matchedWorkflows.map(renderWorkflowResult)}
              </React.Fragment>
            );
          })}
        </CommandGroup>
      )}
      {/* Cold search: fetching with no hits on screen yet. Placeholder rows
          hold the list open (under the commands, if any matched); a search
          that already has hit rows (stale results during a re-query, or a
          section that landed first) keeps them and never flashes a
          skeleton. */}
      {loading && !hasHits && <SearchResultsSkeleton />}
      {!loading && !hasAnyResult && (
        <CommandEmpty>
          <SearchEmptyState
            title="No exact matches found"
            subtext="Try different keywords or change some filters."
            onClearFilters={
              selectedKinds.length > 0 ? clearFilters : undefined
            }
          />
        </CommandEmpty>
      )}
    </React.Fragment>)
  ) : browsing ? (
    // Chip browse: empty query + selected chips. The launcher swaps to a
    // Results list of the selected kinds from client-side workspace data, so
    // a chip press always visibly acts. Keyed like typed search so
    // re-chipping remounts the list in DOM order (keyboard-nav order).
    (<React.Fragment key={`browse-${selectedKinds.join(",")}`}>
      {browseUsers.length > 0 ||
      browseTeams.length > 0 ||
      (selectedKinds.includes("issue") && recentResults.length > 0) ||
      selectedKinds.includes("address") ? (
        <CommandGroup>
          <CommandGroupLabel>Results</CommandGroupLabel>
          {visibleFilters.map(({ value: kind }) => {
            switch (kind) {
              case "issue":
                // Projects have no client-side "all" list — recents are what
                // browse can honestly show without a server call.
                return (
                  <React.Fragment key={kind}>
                    {recentResults.map(renderRecentResult)}
                  </React.Fragment>
                );
              case "user":
                return (
                  <React.Fragment key={kind}>
                    {browseUsers.map(renderBrowseUser)}
                  </React.Fragment>
                );
              case "team":
                return (
                  <React.Fragment key={kind}>
                    {browseTeams.map(renderBrowseTeam)}
                  </React.Fragment>
                );
              case "address":
                // Addresses can't browse — Google Places needs query text —
                // so the chip answers with a hint instead of dead air.
                return (
                  <Box
                    key={kind}
                    direction="row"
                    align="center"
                    gap="sm"
                    paddingX="sm"
                    paddingY="xs"
                  >
                    <Box
                      width={ROW_VISUAL_BOX}
                      height={ROW_VISUAL_BOX}
                      align="center"
                      justify="center"
                      shrink={false}
                    >
                      <Icon name="map-pin" size="sm" color="muted" />
                    </Box>
                    <Text size="sm" variant="muted">
                      Type to search addresses...
                    </Text>
                  </Box>
                );
              default:
                return null;
            }
          })}
        </CommandGroup>
      ) : (
        <CommandEmpty>
          <SearchEmptyState
            title="Nothing to show yet"
            subtext="Type to search, or clear the filters to browse."
            onClearFilters={clearFilters}
          />
        </CommandEmpty>
      )}
    </React.Fragment>)
  ) : (
    // Empty state / slash mode (the launcher). "Results" (recents, else the
    // default workflow) leads on the true empty state only — typing (slash or
    // pre-debounce text) narrows to commands.
    (<>
      {query.trim().length === 0 &&
        (recentResults.length > 0 ? (
          <CommandGroup>
            <CommandGroupLabel>Results</CommandGroupLabel>
            {recentResults.map(renderRecentResult)}
          </CommandGroup>
        ) : fallbackWorkflow ? (
          <CommandGroup>
            <CommandGroupLabel>Results</CommandGroupLabel>
            {renderWorkflowResult(fallbackWorkflow)}
          </CommandGroup>
        ) : null)}
      {filteredCommands.length > 0 ? (
        <CommandGroup>
          <CommandGroupLabel>Commands</CommandGroupLabel>
          {filteredCommands.map(renderCommand)}
        </CommandGroup>
      ) : (
        // A slash query that matches no command.
        (<CommandEmpty>No commands found.</CommandEmpty>)
      )}
    </>)
  );

  // Scoped-search chips. Always shown (even on the empty launcher), pinned
  // at the panel's top on every surface — the list scrolls beneath them, so
  // the active scopes stay readable however deep the results run.
  const scopeBar = (
    // On the panel's 16px content grid, matching the footer; 12px of air
    // above and below keeps the pinned row from crowding the input seam or
    // the first result.
    <Box direction="row" align="center" gap="xs" paddingX="md" paddingY="base">
      {/* Pill chips resting on the dialog surface. The root's state ink
          (muted → full foreground when pressed) colors label and glyph
          together — so the Text takes no variant and inherits. */}
      {availableFilters.map((filter) => {
        const isSelected = selectedKinds.includes(filter.value);
        return (
          <Toggle
            key={filter.value}
            size="xs"
            variant="outline"
            shape="pill"
            pressed={isSelected}
            onPressedChange={() => toggleKind(filter.value)}
          >
            <Icon name={SEARCH_KIND_META[filter.value].icon} size="sm" />
            <Text size="xs">{filter.label}</Text>
          </Toggle>
        );
      })}
    </Box>
  );

  return {
    query,
    setQuery,
    itemsContent,
    scopeBar,
    footerContext: highlightedValue
      ? contextByValue[highlightedValue]
      : undefined,
    footerEnterVerb:
      highlightedValue?.startsWith("address-") && addressCreates
        ? "create"
        : "open",
    onItemHighlighted,
    onInputKeyDown,
    listRef,
    feedbackOpen,
    setFeedbackOpen,
  };
}
